import { Injectable } from '@angular/core';
import { Storage, deleteObject, getDownloadURL, ref, uploadBytes, uploadBytesResumable } from '@angular/fire/storage';
import { FirebaseError } from 'firebase/app';
import { UserService } from '../user services/user.service';
import { firstValueFrom, Observable } from 'rxjs';
import { UploadEvent } from '../../interfaces/user-generated-content.model';
import { AnonymousUserService } from '../user services/anonymous-user.service';


@Injectable({
  providedIn: 'root'
})



export class StorageService {

  private user = {
    uid: '',
    picture: '',
  };

  constructor(
    private _storage: Storage,
    private _anonymousUserService: AnonymousUserService,
    private _userService: UserService,
  ) {

  }

  async fetchUserPicture(): Promise<string | null> {
    this._anonymousUserService.currentUserId;
    if (!this.user.uid) {
      console.warn('No user UID available, cannot fetch picture.');
      return null;
    }

    const extensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const fileName = `userPictures/${this.user.uid}`;
    const promises = extensions.map(ext => getDownloadURL(ref(this._storage, `${fileName}.${ext}`)));

    const results = await Promise.allSettled(promises);
    for (const result of results) {
      if (result.status === 'fulfilled') {
        return result.value;
      }
    }

    console.warn('No user picture found with any of the tried extensions.');
    return null;
  }


  async getUserPictureUrl(): Promise<string | null> {
    return await this.fetchUserPicture(); // Fetch and return the picture URL
  }


  async getDownloadURL(path: string): Promise<string> {
    try {
      const storageRef = ref(this._storage, path);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('[StorageService] Error getting download URL:', error);
      throw error;
    }
  }

  async uploadUserPicture(uid: string, userPicture: string | ArrayBuffer): Promise<string> {
    try {
      const currentUid = this._anonymousUserService.currentUserId;

      if (!currentUid) {
        throw new Error('User not initialized. App must complete initialization first.');
      }

      uid = currentUid;

      if (!uid || !userPicture) {
        throw new Error('User ID or picture data is missing');
      }

      try {
        const blob = await this.convertToBlob(userPicture);
        const timestamp = Date.now();
        const filename = `profile_${uid}_${timestamp}.jpg`;
        const filePath = `userPictures/${uid}/${filename}`;
        const storageRef = ref(this._storage, filePath);

        const metadata = {
          contentType: 'image/jpeg',
          customMetadata: {
            'uploadedBy': uid,
            'uploadedAt': new Date().toISOString()
          }
        };

        const uploadResult = await uploadBytes(storageRef, blob, metadata);
        const downloadURL = await getDownloadURL(uploadResult.ref);
        return downloadURL;
      } catch (error) {
        if (error instanceof FirebaseError) {
          console.error('Firebase error:', {
            code: error.code,
            message: error.message,
            uid
          });
          throw error;
        } else {
          console.error('Non-Firebase error:', {
            error: error instanceof Error ? error.message : 'Unknown error',
            uid
          });
          throw new Error(error instanceof Error ? error.message : 'Unknown upload error');
        }
      }
    } catch (error) {
      console.error('Error in uploadUserPicture:', error);
      throw error;
    }
  }

  async convertToBlob(data: string | ArrayBuffer): Promise<Blob> {
    try {
      if (typeof data === 'string') {
        // Handle base64 data URL
        if (data.startsWith('data:')) {
          // Remove the data URL prefix if present
          const response = await fetch(data);
          return await response.blob();
        } else {
          // Handle raw base64 string
          const byteString = atob(data);
          const arrayBuffer = new ArrayBuffer(byteString.length);
          const uint8Array = new Uint8Array(arrayBuffer);
          for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
          }
          return new Blob([arrayBuffer], { type: 'image/jpeg' });
        }
      } else if (data instanceof ArrayBuffer) {
        return new Blob([data], { type: 'image/jpeg' });
      }
      throw new Error('Unsupported data format');
    } catch (error) {
      console.error('Error converting to blob:', error);
      throw error;
    }
  }

  async deletePhotoFromStorage(photoUrl: string): Promise<void> {
    if (!photoUrl) {
      throw new Error('Photo URL is missing');
    }

    try {
      // Extract the storage path from the download URL
      // Firebase download URLs contain the path and token information
      const storageRef = ref(this._storage, this.getPathFromUrl(photoUrl));

      await deleteObject(storageRef);
      console.log(`[StorageService] Photo successfully deleted from Storage`);
    } catch (error) {
      if (error instanceof FirebaseError) {
        // Handle specific Firebase Storage errors
        if (error.code === 'storage/object-not-found') {
          console.log(`[StorageService] Photo already deleted or doesn't exist in Storage`);
          return; // Exit silently as the file is already gone
        }
        console.error('[StorageService] Firebase error deleting photo:', {
          code: error.code,
          message: error.message,
          photoUrl
        });
        throw error;
      } else {
        console.error('[StorageService] Non-Firebase error deleting photo:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          photoUrl
        });
        throw new Error(error instanceof Error ? error.message : 'Storage deletion failed');
      }
    }
  }

  async deleteVideoFromStorage(videoUrl: string): Promise<void> {
    if (!videoUrl) {
      throw new Error('video URL is missing');
    }

    try {
      // Extract the storage path from the download URL
      // Firebase download URLs contain the path and token information
      const storageRef = ref(this._storage, this.getPathFromUrl(videoUrl));

      await deleteObject(storageRef);
      console.log(`[StorageService] video successfully deleted from Storage`);
    } catch (error) {
      if (error instanceof FirebaseError) {
        // Handle specific Firebase Storage errors
        if (error.code === 'storage/object-not-found') {
          console.log(`[StorageService] video already deleted or doesn't exist in Storage`);
          return; // Exit silently as the file is already gone
        }
        console.error('[StorageService] Firebase error deleting video:', {
          code: error.code,
          message: error.message,
          videoUrl
        });
        throw error;
      } else {
        console.error('[StorageService] Non-Firebase error deleting video:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          videoUrl
        });
        throw new Error(error instanceof Error ? error.message : 'Storage deletion failed');
      }
    }
  }

  async deleteVideoFromStorageByPath(filePath: string): Promise<void> {
    try {
      // Create a reference to the file
      const storageRef = ref(this._storage, filePath);

      // Delete the file
      await deleteObject(storageRef);
      console.log(`[FirestoreService] video successfully deleted from Storage: ${filePath}`);
    } catch (error) {
      if (error instanceof FirebaseError) {
        // Handle specific Firebase Storage errors
        if (error.code === 'storage/object-not-found') {
          console.log('[FirestoreService] video already deleted or doesn\'t exist in Storage');
          return; // Exit silently as the file is already gone
        }
        console.error('[FirestoreService] Firebase error deleting video from Storage:', error);
      } else {
        console.error('[FirestoreService] Error deleting video from Storage:', error);
      }
      throw error;
    }
  }

  async uploadVideoToStorage(formData: FormData, latitude: number, longitude: number): Promise<Observable<UploadEvent>> {
    const userId = this._anonymousUserService.currentUserId;

    if (!userId) {
      throw new Error('User not initialized');
    }

    // Generate a unique document ID
    const docId = this.generateUniqueId();

    // Extract the video file from the FormData
    const videoFile = formData.get('video') as File;
    if (!videoFile) {
      throw new Error('No video file provided');
    }

    // Check if the file exceeds the size limit (50MB)
    if (videoFile.size > 50 * 1024 * 1024) {
      throw new Error('Video file size exceeds the maximum limit of 50MB');
    }

    // Check if the file is a video
    if (!videoFile.type.startsWith('video/')) {
      throw new Error('The selected file is not a valid video');
    }

    // Create a storage reference
    const storageRef = ref(this._storage, `videos/${userId}/${docId}`);

    // Create an upload task with progress monitoring
    const uploadTask = uploadBytesResumable(storageRef, videoFile);

    // Monitor the upload progress
    return new Observable<UploadEvent>(observer => {
      uploadTask.on('state_changed',
        // Progress handler
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next({
            type: 'uploadProgress',
            loaded: progress,
            total: 100
          } as UploadEvent);
        },
        // Error handler
        (error) => {
          observer.error(error);
        },
        // Completion handler
        async () => {
          try {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

            observer.next({
              type: 'complete',
              url: downloadUrl
            } as UploadEvent);

            observer.complete();
          } catch (error) {
            observer.error(error);
          }
        }
      );
    });
  }

  async deleteAudioFromStorage(audioUrl: string): Promise<void> {
    if (!audioUrl) {
      throw new Error('audio URL is missing');
    }

    try {
      // Extract the storage path from the download URL
      // Firebase download URLs contain the path and token information
      const storageRef = ref(this._storage, this.getPathFromUrl(audioUrl));

      await deleteObject(storageRef);
      console.log(`[StorageService] audio successfully deleted from Storage`);
    } catch (error) {
      if (error instanceof FirebaseError) {
        // Handle specific Firebase Storage errors
        if (error.code === 'storage/object-not-found') {
          console.log(`[StorageService] audio already deleted or doesn't exist in Storage`);
          return; // Exit silently as the file is already gone
        }
        console.error('[StorageService] Firebase error deleting audio:', {
          code: error.code,
          message: error.message,
          audioUrl
        });
        throw error;
      } else {
        console.error('[StorageService] Non-Firebase error deleting audio:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          audioUrl
        });
        throw new Error(error instanceof Error ? error.message : 'Storage deletion failed');
      }
    }
  }

  async deleteAudioFromStorageByPath(filePath: string): Promise<void> {
    try {
      // Create a reference to the file
      const storageRef = ref(this._storage, filePath);

      // Delete the file
      await deleteObject(storageRef);
      console.log(`[StorageService] audio successfully deleted from Storage: ${filePath}`);
    } catch (error) {
      if (error instanceof FirebaseError) {
        // Handle specific Firebase Storage errors
        if (error.code === 'storage/object-not-found') {
          console.log('[StorageService] audio already deleted or doesn\'t exist in Storage');
          return; // Exit silently as the file is already gone
        }
        console.error('[StorageService] Firebase error deleting audio from Storage:', error);
      } else {
        console.error('[StorageService] Error deleting audio from Storage:', error);
      }
      throw error;
    }
  }

  async uploadAudioToStorage(formData: FormData, latitude: number, longitude: number): Promise<Observable<UploadEvent>> {
    const userId = this._anonymousUserService.currentUserId;

    if (!userId) {
      throw new Error('User not initialized');
    }
    // Generate a unique document ID (using custom method since createId() is not available in v9+)
    const docId = this.generateUniqueId();

    // Extract the audio file from the FormData
    const audioFile = formData.get('audio') as File;
    if (!audioFile) {
      throw new Error('No audio file provided');
    }

    // Check if the file exceeds the size limit (50MB)
    if (audioFile.size > 50 * 1024 * 1024) {
      throw new Error('audio file size exceeds the maximum limit of 50MB');
    }

    // Check if the file is a audio
    if (!audioFile.type.startsWith('audio/')) {
      throw new Error('The selected file is not a valid audio');
    }

    // Create a storage reference
    const storageRef = ref(this._storage, `audios/${userId}/${docId}`);

    // Create an upload task with progress monitoring
    const uploadTask = uploadBytesResumable(storageRef, audioFile);

    // Monitor the upload progress
    return new Observable<UploadEvent>(observer => {
      uploadTask.on('state_changed',
        // Progress handler
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next({
            type: 'uploadProgress',
            loaded: progress,
            total: 100
          } as UploadEvent);
        },
        // Error handler
        (error) => {
          observer.error(error);
        },
        // Completion handler
        async () => {
          try {
            // Get the download URL
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

            observer.next({
              type: 'complete',
              url: downloadUrl
            } as UploadEvent);

            // Complete the observable
            observer.complete();
          } catch (error) {
            observer.error(error);
          }
        }
      );
    });
  }

  private generateUniqueId(): string {
    // This is a simplified version of Firestore's auto-ID generation
    // You might want to use a more robust library like uuid instead
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 20; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }


  private getPathFromUrl(url: string): string {
    try {
      // First find indices in the original URL
      const pathStartIndex = url.indexOf('/o/') + 3;
      const pathEndIndex = url.indexOf('?', pathStartIndex);

      if (pathStartIndex > 2 && pathEndIndex > pathStartIndex) {
        // Extract the encoded path
        const encodedPath = url.substring(pathStartIndex, pathEndIndex);
        // Then decode only the path portion
        return decodeURIComponent(encodedPath);
      }

      throw new Error('Invalid Firebase Storage URL format');
    } catch (error) {
      console.error('Error parsing storage URL:', error);
      throw new Error('Could not determine storage path from URL');
    }
  }

  async deletePhotoFromStorageByPath(filePath: string): Promise<void> {
    try {
      // Create a reference to the file
      const storageRef = ref(this._storage, filePath);

      // Delete the file
      await deleteObject(storageRef);
      console.log(`[FirestoreService] Photo successfully deleted from Storage: ${filePath}`);
    } catch (error) {
      if (error instanceof FirebaseError) {
        // Handle specific Firebase Storage errors
        if (error.code === 'storage/object-not-found') {
          console.log('[FirestoreService] Photo already deleted or doesn\'t exist in Storage');
          return; // Exit silently as the file is already gone
        }
        console.error('[FirestoreService] Firebase error deleting photo from Storage:', error);
      } else {
        console.error('[FirestoreService] Error deleting photo from Storage:', error);
      }
      throw error;
    }
  }
}